/**
 * ADR-168 D10 — the refusal routes become ACTIONS.
 *
 * The Data area used to render a route as bold text at the end of a sentence, and only when the
 * whole set was read-only. ADR-121 D1's rule is that a capability existing only in JSON is not a
 * capability the editor's users have; a route that is only prose is the same failure, and it was
 * worse than prose for `fleet-channels@v1` — editable at the values layer, denied at the CONTRACT
 * layer — because the read-only condition never fired and NOTHING rendered.
 *
 * Two things this is deliberately not:
 *
 *  1. NOT part of `SchemaAddFields`. That panel is not mounted when `schema_publish.state` is
 *     `denied`, which is exactly when the contract route is needed. This is a shared component
 *     the host surface renders directly.
 *  2. NOT a bare button. A conversation seeded with "this fleet may not change this schema" and
 *     nothing else asks the receiving agent to guess the request. The operator's stated need is
 *     collected FIRST and travels with the seed, along with the two things a chat window cannot
 *     look up for itself: the exact schema ref plus version, and who is already pinned to it.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type DataSetRow, type DataUsedBy, type SchemaPublishVerdict } from '../api/client'

/** The agent a refusal hands off to, or `null` when the destination does not exist.
 *
 *  `unavailable` is NOT `denied` and must not collapse into it: nobody has said this fleet may
 *  not publish, the schema could not be READ. Core cannot act on that — the fleet's own
 *  reference-data capability (the `reference-data-authoring` skill, bound to `fleet-architect`
 *  in platform-manifest.yaml) is what repairs a schema, which is why `data_area_rules.py`'s
 *  `READINESS_ROUTE` is the fleet's route and not core's.
 *
 *  `external_fleet_instance` returns `null` on purpose. Its route is the fleet's MCP Admin,
 *  which is unbuilt — the router registers 19 paths and none is it — and zero external sets
 *  exist, so an action would lead nowhere. Text with the reason is the honest rendering. */
function handOffAgentFor(
  klass: DataSetRow['klass'],
  publishState: SchemaPublishVerdict['state'] | undefined,
): string | null {
  if (klass === 'external_fleet_instance') return null
  if (publishState === 'unavailable') return 'fleet-architect'
  return 'core-advisor'
}

export interface ProposeToCoreActionProps {
  /** The set ref, e.g. `fleet-channels@v1`. */
  set: string
  /** The SCHEMA ref, e.g. `fleet-channel@v1` — what the receiving agent has to change. */
  schema: string
  klass: DataSetRow['klass']
  /** Why, in operator language. Rendered as-is; the server owns the wording. */
  reason: string | null
  /** The ONE next step that works. Becomes the control's label. */
  route: string
  /** ADR-168 D2's contract verdict, when the denial is at the contract layer. */
  publishState?: SchemaPublishVerdict['state']
  /** The used-by answer, when the HOST already read it. The seed needs the pin list, and a host
   *  that has it should not make the operator wait for a second read of the same endpoint. Left
   *  absent, this component reads it itself — which is what the call sites that have no such
   *  answer to hand still do. */
  usedBy?: DataUsedBy
}

/** The version half of a `name@vN` ref. `unknown` rather than a guess — the seed says what it
 *  knows and nothing more. */
function versionOf(ref: string): string {
  const at = ref.lastIndexOf('@')
  return at >= 0 ? ref.slice(at + 1) : 'unknown'
}

export function ProposeToCoreAction(props: ProposeToCoreActionProps) {
  const { set, schema, klass, reason, route, publishState, usedBy } = props
  const navigate = useNavigate()
  const agent = handOffAgentFor(klass, publishState)

  const [open, setOpen] = useState(false)
  const [need, setNeed] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!agent) {
    return (
      <div className="mb-3" data-testid="propose-route">
        <p className="text-sm text-gray-600" data-testid="propose-route-text">
          {reason ? `${reason} ` : ''}{route}.
        </p>
      </div>
    )
  }

  const send = async () => {
    setSending(true)
    setError(null)
    try {
      // A FAILED used-by read is never seeded as "nothing uses this": that is the answer which
      // unblocks a retirement, and it must not be manufactured from a 503 (ADR-127 D4).
      let usedByLines: string
      try {
        // The host's answer when it has one — the panel that mounts this has already read the
        // same endpoint for its staying-consumers sentence, and refetching it here would ask
        // twice for one answer.
        const answer = usedBy ?? await api.getDataReferenceUsedBy(set)
        usedByLines = answer.consumers.length === 0
          ? 'Used by: nothing declares a pin to this set.'
          : [`Used by (${answer.count}):`, ...answer.consumers.map(c => {
            const via = c.indirect ? ` — indirect, via ${c.via ?? 'a workflow'}` : ''
            return `- ${c.kind} ${c.name} (${c.path}:${c.line})${via}`
          })].join('\n')
        if (!answer.role_bindings_readable) {
          usedByLines += '\n- NOTE: the registry policy could not be parsed, so a role-bound '
            + 'consumer may be missing from this list.'
        }
      } catch {
        usedByLines = 'Used by: the consumer list could not be read, so it is NOT known that '
          + 'nothing depends on this set.'
      }

      const seed = [
        `The Fleet Editor's Data area refused a schema change and routed it here: ${route}.`,
        '',
        `Set: ${set}`,
        `Schema: ${schema}`,
        `Current version: ${versionOf(schema)}`,
        `Why this fleet cannot do it itself: ${reason ?? 'not stated'}`,
        '',
        usedByLines,
        '',
        'What the operator needs:',
        need.trim(),
      ].join('\n')

      const conv = await api.createConversation({
        title: `${route}: ${set}`,
        primary_agent: agent,
      })
      await api.sendMessage(conv.id, seed, { composed: true })
      navigate(`/chat?conversation=${encodeURIComponent(conv.id)}`)
    } catch (e) {
      setError(`The hand-off could not be started: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mb-3" data-testid="propose-route">
      {reason && (
        <p className="text-sm text-gray-600" data-testid="propose-route-reason">{reason}</p>
      )}
      {!open ? (
        <button
          type="button"
          className="mt-1 px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50"
          data-testid="propose-route-open"
          onClick={() => setOpen(true)}
        >
          {route}
        </button>
      ) : (
        <div className="mt-2">
          <label className="block text-xs text-gray-500" htmlFor="propose-route-need">
            What do you need this schema to do? The agent receives this with the set, its
            version and its current consumers.
          </label>
          <textarea
            id="propose-route-need"
            data-testid="propose-route-need"
            className="mt-1 w-full text-sm border border-gray-300 rounded p-2"
            rows={3}
            value={need}
            onChange={e => setNeed(e.target.value)}
          />
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              className="px-2 py-1 text-sm rounded bg-gray-900 text-white disabled:opacity-40"
              data-testid="propose-route-send"
              disabled={!need.trim() || sending}
              onClick={send}
            >
              {sending ? 'Starting…' : `Send to ${agent}`}
            </button>
            <button
              type="button"
              className="px-2 py-1 text-sm rounded border border-gray-300"
              data-testid="propose-route-cancel"
              onClick={() => { setOpen(false); setError(null) }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-700" data-testid="propose-route-error">{error}</p>
      )}
    </div>
  )
}
